/**
 * @file src/shell/SettingsPanel.js
 * Bảng CÀI ĐẶT dùng chung — 6 mục:
 *   âm thanh · âm lượng · ngôn ngữ · MÀU NỀN · xoay · tốc độ chuyển động
 *
 * Tiết KHÔNG tự dựng các mục này. Tiết chỉ:
 *   ctx.ui.addSetting({...})   ← thêm mục ĐẶC THÙ của tiết
 */

import { icon } from './icons.js';

/**
 * Hai màu nền mặc định — ĐEN và TRẮNG, hai đầu của dải. Ai cần màu khác thì dùng
 * bảng chọn màu ngay cạnh (ô thứ ba).
 * config.js của tiết quyết định màu KHỞI ĐẦU; từ lúc học sinh bấm vào đây thì lựa
 * chọn của học sinh thắng và được lưu lại.
 */

/**
 * @param {Object} o
 * @param {HTMLElement} o.root
 * @param {(k: string, v?: any) => string} o.t
 * @param {import('../core/settings.js').Settings} o.settings
 * @param {string[]} o.languages
 * @param {import('./registry.js').Registry<any>} o.extra
 * @param {(open: boolean) => void} [o.onOpenChange]
 */
export function createSettingsPanel(o) {
  const el = document.createElement('aside');
  el.className = 'ol-settings';
  el.innerHTML = `
    <div class="ol-settings__head">
      <span class="ol-settings__title"></span>
      <button class="ol-btn ol-btn--icon ol-settings__close"><span class="ol-icon">${icon('close')}</span></button>
    </div>
    <div class="ol-settings__body"></div>`;
  o.root.appendChild(el);
  // Sinh ra là đang ĐÓNG → inert ngay, không chờ close() lần đầu.
  el.inert = true;

  /**
   * Hàm gỡ của các listener settings mà render() tạo ra. render() dựng lại toàn bộ
   * body nên PHẢI gỡ hết trước khi dựng lại, không thì listener cũ trỏ vào node đã
   * bị bỏ và cứ tích tụ mỗi lần mở bảng.
   * @type {Array<() => void>}
   */
  let unsubs = [];
  const dropSubs = () => {
    for (const u of unsubs) u();
    unsubs = [];
  };

  const body = /** @type {HTMLElement} */ (el.querySelector('.ol-settings__body'));
  const head = /** @type {HTMLElement} */ (el.querySelector('.ol-settings__title'));
  /** @type {HTMLElement} */ (el.querySelector('.ol-settings__close')).addEventListener(
    'click',
    () => close(),
  );

  /**
   * Khối nhóm (ÂM THANH/HIỂN THỊ/CHUYỂN ĐỘNG) — nhãn HOA nhỏ + các hàng bên trong.
   * Tách bảng cài đặt thành từng khối theo file thiết kế, thay vì một danh sách
   * phẳng như bản trước (dễ đọc hơn khi có 6 mục).
   * @param {string} titleKey @param {HTMLElement[]} rows
   */
  function section(titleKey, rows) {
    const s = document.createElement('div');
    s.className = 'ol-set__section';
    const t = document.createElement('div');
    t.className = 'ol-set__section-title';
    t.textContent = o.t(titleKey);
    s.appendChild(t);
    for (const r of rows) s.appendChild(r);
    return s;
  }

  /** Đường kẻ mảnh giữa hai khối. */
  function divider() {
    const d = document.createElement('div');
    d.className = 'ol-set__divider';
    return d;
  }

  /** @param {string} labelKey */
  function row(labelKey) {
    const r = document.createElement('div');
    r.className = 'ol-set__row';
    const l = document.createElement('span');
    l.className = 'ol-set__label';
    l.textContent = o.t(labelKey);
    r.appendChild(l);
    return r;
  }

  /** @param {string} labelKey @param {import('../core/settings.js').SettingKey} key */
  function toggle(labelKey, key) {
    const r = row(labelKey);
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ol-switch';
    const sync = () => {
      const on = Boolean(o.settings.get(key));
      btn.classList.toggle('is-on', on);
      btn.setAttribute('aria-pressed', String(on));
      // KHÔNG ghi "Bật"/"Tắt" vào nút nữa. Chữ đó mơ hồ: viên ghi "Tắt" đọc được
      // thành cả "đang tắt" lẫn "bấm để tắt". Núm gạt đã nói lên trạng thái bằng vị
      // trí; phần còn lại là việc của aria-pressed, dành cho trình đọc màn hình.
      btn.setAttribute('aria-label', `${o.t(labelKey)} — ${o.t(on ? 'set.on' : 'set.off')}`);
    };
    btn.addEventListener('click', () => {
      o.settings.set(key, !o.settings.get(key));
      sync();
    });
    sync();
    // Nút cùng tuỳ chọn ở taskbar có thể đổi giá trị → hàng này phải nghe theo,
    // không thì hai chỗ hiện hai trạng thái khác nhau.
    unsubs.push(o.settings.onChange(key, sync));
    r.appendChild(btn);
    return r;
  }

  /**
   * Hàng thanh trượt cho tuỳ chọn số của SHELL.
   * @param {string} labelKey
   * @param {import('../core/settings.js').SettingKey} key
   * @param {{min: number, max: number, step: number, format?: (v: number) => string}} cfg
   */
  function range(labelKey, key, cfg) {
    const r = row(labelKey);
    const wrap = document.createElement('div');
    wrap.className = 'ol-set__range';
    const input = document.createElement('input');
    input.type = 'range';
    input.className = 'ol-range';
    input.min = String(cfg.min);
    input.max = String(cfg.max);
    input.step = String(cfg.step);
    const out = document.createElement('span');
    out.className = 'ol-set__value';

    const fmt = cfg.format ?? ((v) => String(v));
    const sync = () => {
      const v = Number(o.settings.get(key));
      input.value = String(v);
      out.textContent = fmt(v);
    };
    input.addEventListener('input', () => {
      o.settings.set(key, Number(input.value));
      out.textContent = fmt(Number(input.value));
    });
    sync();
    unsubs.push(o.settings.onChange(key, sync));

    input.setAttribute('aria-label', o.t(labelKey));
    wrap.append(input, out);
    r.appendChild(wrap);
    return r;
  }

  function render() {
    dropSubs();
    head.textContent = o.t('set.title');
    body.innerHTML = '';

    // 1. Âm thanh + âm lượng. Âm lượng mờ đi khi tắt tiếng, nhưng GIÁ TRỊ vẫn giữ.
    const soundToggleRow = toggle('set.sound', 'sound');
    const volRow = range('set.volume', 'volume', {
      min: 0,
      max: 1,
      step: 0.05,
      format: (v) => `${Math.round(v * 100)}%`,
    });
    const syncVolEnabled = () => {
      const on = Boolean(o.settings.get('sound'));
      volRow.classList.toggle('is-disabled', !on);
      const inp = /** @type {HTMLInputElement | null} */ (volRow.querySelector('input'));
      if (inp) inp.disabled = !on;
    };
    syncVolEnabled();
    unsubs.push(o.settings.onChange('sound', syncVolEnabled));
    body.appendChild(section('set.section.sound', [soundToggleRow, volRow]));
    body.appendChild(divider());

    // 2. Ngôn ngữ (chỉ hiện khi tiết có >1 ngôn ngữ) + màu nền — cùng khối "Hiển thị".
    const displayRows = [];
    if (o.languages.length > 1) {
      const r = row('set.language');
      const sel = document.createElement('select');
      sel.className = 'ol-select';
      for (const l of o.languages) {
        const opt = document.createElement('option');
        opt.value = l;
        opt.textContent = o.t(`lang.${l}`);
        if (l === o.settings.get('lang')) opt.selected = true;
        sel.appendChild(opt);
      }
      sel.addEventListener('change', () => o.settings.set('lang', sel.value));
      r.appendChild(sel);
      displayRows.push(r);
    }

    // 3. Màu nền — ĐÃ GỠ khỏi panel theo yêu cầu. Khoá `background` VẪN CÒN trong
    // core/settings.js và vẫn có tác dụng: tiết đặt màu khởi đầu bằng
    // ctx.settings.setDefault('background', …), Shell vẫn theo đó để gắn .is-stage-dark.
    // Chỉ bỏ chỗ cho học sinh tự đổi — mô hình thường có vòm trời riêng phủ kín khung
    // nhìn nên màu nền gần như không thấy, mà lại là một hàng dễ bấm nhầm.

    body.appendChild(section('set.section.display', displayRows));
    body.appendChild(divider());

    // 4. Xoay + tốc độ chuyển động.
    // Hàng này thay cho "Giảm chuyển động" cũ: cùng một nút với nút xoay ở taskbar
    // (chung khoá settings.rotate). reducedMotion vẫn còn trong core và vẫn tự bật
    // theo prefers-reduced-motion của hệ điều hành, chỉ không còn hàng riêng ở đây —
    // tắt "Xoay" đã cho đúng kết quả mà học sinh muốn.
    const rotateToggleRow = toggle('set.rotate', 'rotate');
    const spdRow = range('set.motionSpeed', 'motionSpeed', {
      min: 0.2,
      max: 2,
      step: 0.1,
      format: (v) => `${v.toFixed(1)}×`,
    });
    const syncSpdEnabled = () => {
      // Kéo tốc độ vô nghĩa khi đã tắt xoay, hoặc khi OS yêu cầu giảm chuyển động.
      const off = !o.settings.get('rotate') || Boolean(o.settings.get('reducedMotion'));
      spdRow.classList.toggle('is-disabled', off);
      const inp = /** @type {HTMLInputElement | null} */ (spdRow.querySelector('input'));
      if (inp) inp.disabled = off;
    };
    syncSpdEnabled();
    unsubs.push(o.settings.onChange('rotate', syncSpdEnabled));
    unsubs.push(o.settings.onChange('reducedMotion', syncSpdEnabled));
    body.appendChild(section('set.section.motion', [rotateToggleRow, spdRow]));

    // 5. Mục đặc thù do TIẾT thêm — KHÔNG có khối cha riêng (nội dung tiết tự do,
    // không thuộc 3 nhóm cố định trên), chỉ ngăn cách bằng một đường kẻ nếu tiết CÓ
    // đăng ký gì thật.
    const extraSlots = o.extra.list();
    if (extraSlots.length) body.appendChild(divider());
    for (const slot of extraSlots) {
      const d = slot.data;
      const r = row(d.labelKey);
      if (d.type === 'toggle') {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'ol-switch';
        let v = Boolean(d.value);
        // Giống hệt toggle() ở trên — mục của tiết cũng phải là núm gạt, không thì
        // bảng cài đặt có hai kiểu công tắc khác nhau.
        const sync = () => {
          btn.classList.toggle('is-on', v);
          btn.setAttribute('aria-pressed', String(v));
          btn.setAttribute('aria-label', `${o.t(d.labelKey)} — ${o.t(v ? 'set.on' : 'set.off')}`);
        };
        btn.addEventListener('click', () => {
          v = !v;
          sync();
          d.onChange?.(v);
        });
        sync();
        r.appendChild(btn);
      } else if (d.type === 'select') {
        const sel = document.createElement('select');
        sel.className = 'ol-select';
        for (const opt of d.options ?? []) {
          const oe = document.createElement('option');
          oe.value = String(opt.value ?? opt);
          oe.textContent = o.t(String(opt.labelKey ?? opt));
          if (oe.value === String(d.value)) oe.selected = true;
          sel.appendChild(oe);
        }
        sel.addEventListener('change', () => d.onChange?.(sel.value));
        r.appendChild(sel);
      }
      body.appendChild(r);
    }
  }

  // Xem chú thích cùng tên trong InfoPanel.js: phải phát từ đây vì panel tự đóng
  // được bằng dấu × bên trong.
  const emitOpen = () => o.onOpenChange?.(el.classList.contains('is-open'));
  // Panel ĐÓNG phải là `inert`. Nó chỉ được translateX ra ngoài rồi khung .ol-panelhost
  // cắt đi, nên với người dùng bàn phím thì 10 control bên trong VẪN Tab vào được —
  // focus rơi vào một giao diện vô hình. Đã kiểm: gọi .focus() vào công tắc trong panel
  // đóng thì document.activeElement đúng là nó.
  // inert khoá cả focus, chuột và trình đọc màn hình bằng một thuộc tính; không cần
  // aria-hidden riêng (dùng cả hai còn dễ lệch nhau).
  const open = () => {
    render();
    el.inert = false;
    el.classList.add('is-open');
    emitOpen();
  };
  const close = () => {
    el.classList.remove('is-open');
    el.inert = true;
    emitOpen();
  };
  const off = o.extra.onChange(() => {
    if (el.classList.contains('is-open')) render();
  });
  render();

  return {
    el,
    open,
    close,
    toggle: () => (el.classList.contains('is-open') ? close() : open()),
    retranslate: render,
    dispose() {
      off();
      dropSubs();
      el.remove();
    },
  };
}
